import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Icon } from '../../ui/Icon';
import { LocationForm } from './LocationForm';

export function LocationsPage() {
  const qc = useQueryClient();
  const gridRef = useRef<AgGridReact | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations', showInactive],
    queryFn: () =>
      api.get('/locations', { params: showInactive ? {} : { activeOnly: 'true' } }).then((r) => r.data),
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/locations/${id}`, { active }).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      toast.success(vars.active ? 'Location reactivated' : 'Location deactivated');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/locations/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      toast.success('Location deleted');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? 'Delete failed'),
  });

  const cols = useMemo<ColDef[]>(
    () => [
      { headerName: 'Name', field: 'city', flex: 1, minWidth: 200 },
      {
        headerName: 'Status',
        field: 'active',
        width: 110,
        cellRenderer: (p: any) => (
          <span className={p.value ? 'pill-success' : 'pill-muted'}>
            {p.value ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        pinned: 'right',
        width: 240,
        sortable: false,
        cellRenderer: (p: any) => (
          <div className="flex h-full items-center gap-3 text-xs">
            <button
              className="text-brand-700 hover:underline"
              onClick={() => { setEditing(p.data); setFormOpen(true); }}
            >
              Edit
            </button>
            {p.data.active ? (
              <button
                className="text-rose-600 hover:underline"
                onClick={() => {
                  if (confirm(`Deactivate ${p.data.name}?`)) {
                    setActive.mutate({ id: p.data.id, active: false });
                  }
                }}
              >
                Deactivate
              </button>
            ) : (
              <button
                className="text-emerald-700 hover:underline"
                onClick={() => setActive.mutate({ id: p.data.id, active: true })}
              >
                Reactivate
              </button>
            )}
            <button
              className="text-rose-600 hover:underline"
              onClick={() => {
                if (confirm(`Permanently delete "${p.data.name}"? This cannot be undone.`)) {
                  remove.mutate(p.data.id);
                }
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [setActive, remove],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Locations</h1>
          <p className="text-sm text-ink-subtle">Clinic locations where services are delivered.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button
            className="btn-secondary h-9 whitespace-nowrap"
            onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'locations.csv' })}
          >
            Export CSV
          </button>
          <button
            className="btn-primary h-9 whitespace-nowrap"
            onClick={() => { setEditing(null); setFormOpen(true); }}
          >
            <Icon name="building" size={14} /> New location
          </button>
        </div>
      </header>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        {!isLoading && (locations ?? []).length === 0 ? (
          <div className="grid h-full place-items-center p-8 text-center text-sm text-ink-subtle">
            <div>
              <p className="font-medium text-ink">No locations yet.</p>
              <p className="mt-1">Click <span className="font-medium text-ink">New location</span> to add your first one.</p>
            </div>
          </div>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={locations ?? []}
            columnDefs={cols}
            animateRows
            pagination
            paginationPageSize={50}
            loading={isLoading}
            overlayLoadingTemplate='<span class="text-sm text-slate-500">Loading locations…</span>'
            overlayNoRowsTemplate="No locations found"
          />
        )}
      </div>

      <LocationForm open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}
